import {
    useEffect,
    useState
} from "react";

import api from "../services/api";


function SuperAdmin({
    onLogout
}) {

    const [businesses, setBusinesses] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState("");

    const [showCreateModal, setShowCreateModal] =
        useState(false);

    const [creating, setCreating] =
        useState(false);

    const [updatingId, setUpdatingId] =
        useState(null);


    const [form, setForm] =
        useState({
            business_name: "",
            owner_full_name: "",
            owner_username: "",
            owner_password: ""
        });


    // ========================================
    // LOAD BUSINESSES
    // ========================================

    const loadBusinesses = async () => {

        try {

            setLoading(true);
            setError("");


            const response =
                await api.get(
                    "/super-admin/businesses"
                );


            setBusinesses(
                response.data.businesses || []
            );


        } catch (error) {

            console.error(
                "Failed to load businesses:",
                error
            );


            setError(
                error.response?.data?.message ||
                "Failed to load businesses"
            );


        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadBusinesses();

    }, []);


    // ========================================
    // FORM CHANGE
    // ========================================

    const handleChange = (
        event
    ) => {

        const {
            name,
            value
        } = event.target;


        setForm(
            previous => ({
                ...previous,
                [name]: value
            })
        );
    };


    // ========================================
    // CLOSE CREATE MODAL
    // ========================================

    const closeCreateModal = () => {

        if (creating) {
            return;
        }


        setShowCreateModal(false);

        setError("");

        setForm({
            business_name: "",
            owner_full_name: "",
            owner_username: "",
            owner_password: ""
        });
    };


    // ========================================
    // CREATE BUSINESS
    // ========================================

    const handleCreateBusiness =
        async (event) => {

            event.preventDefault();


            setError("");
            setSuccess("");


            if (
                !form.business_name.trim() ||
                !form.owner_full_name.trim() ||
                !form.owner_username.trim() ||
                !form.owner_password
            ) {

                setError(
                    "Please complete all fields"
                );

                return;
            }


            if (
                form.owner_password.length < 8
            ) {

                setError(
                    "Owner password must be at least 8 characters"
                );

                return;
            }


            try {

                setCreating(true);


                await api.post(
                    "/super-admin/businesses",
                    {
                        business_name:
                            form.business_name.trim(),

                        owner_full_name:
                            form.owner_full_name.trim(),

                        owner_username:
                            form.owner_username.trim(),

                        owner_password:
                            form.owner_password
                    }
                );


                setForm({
                    business_name: "",
                    owner_full_name: "",
                    owner_username: "",
                    owner_password: ""
                });


                setShowCreateModal(false);


                setSuccess(
                    "Business created successfully"
                );


                await loadBusinesses();


            } catch (error) {

                console.error(
                    "Failed to create business:",
                    error
                );


                setError(
                    error.response?.data?.message ||
                    "Failed to create business"
                );


            } finally {

                setCreating(false);
            }
        };


    // ========================================
    // CHANGE BUSINESS STATUS
    // ========================================

    const handleStatusChange =
        async (business) => {

            const nextStatus =
                business.status === "ACTIVE"
                    ? "INACTIVE"
                    : "ACTIVE";


            const action =
                nextStatus === "ACTIVE"
                    ? "activate"
                    : "deactivate";


            const confirmed =
                window.confirm(
                    `Are you sure you want to ${action} ${business.business_name}?`
                );


            if (!confirmed) {
                return;
            }


            try {

                setUpdatingId(
                    business.id
                );

                setError("");
                setSuccess("");


                await api.patch(
                    `/super-admin/businesses/${business.id}/status`,
                    {
                        status:
                            nextStatus
                    }
                );


                setSuccess(
                    nextStatus === "ACTIVE"
                        ? "Business activated successfully"
                        : "Business deactivated successfully"
                );


                await loadBusinesses();


            } catch (error) {

                console.error(
                    "Failed to update business:",
                    error
                );


                setError(
                    error.response?.data?.message ||
                    "Failed to update business"
                );


            } finally {

                setUpdatingId(null);
            }
        };


    // ========================================
    // COUNTS
    // ========================================

    const activeBusinesses =
        businesses.filter(
            business =>
                business.status === "ACTIVE"
        ).length;


    const inactiveBusinesses =
        businesses.filter(
            business =>
                business.status === "INACTIVE"
        ).length;


    // ========================================
    // PAGE
    // ========================================

    return (

        <div className="mobile-app">


            {/* HEADER */}

            <header className="top-header">

                <div>

                    <p className="small-label">
                        PriceManager
                    </p>

                    <h2>
                        Super Admin
                    </h2>

                </div>


                <button
                    type="button"
                    className="logout-button"
                    onClick={
                        onLogout
                    }
                >
                    Logout
                </button>

            </header>


            <main className="super-admin-content">


                {/* WELCOME */}

                <section className="super-admin-welcome">

                    <p>
                        Platform Management
                    </p>

                    <h1>
                        Businesses
                    </h1>

                    <span>
                        Manage PriceManager accounts
                    </span>

                </section>


                {/* SUMMARY */}

                <section className="super-admin-summary">

                    <div className="super-admin-summary-card">

                        <span>
                            Total
                        </span>

                        <strong>
                            {businesses.length}
                        </strong>

                    </div>


                    <div className="super-admin-summary-card active">

                        <span>
                            Active
                        </span>

                        <strong>
                            {activeBusinesses}
                        </strong>

                    </div>


                    <div className="super-admin-summary-card inactive">

                        <span>
                            Inactive
                        </span>

                        <strong>
                            {inactiveBusinesses}
                        </strong>

                    </div>

                </section>


                {/* CREATE BUTTON */}

                <button
                    type="button"
                    className="primary-button super-admin-create-button"
                    onClick={
                        () => {

                            setError("");
                            setSuccess("");

                            setShowCreateModal(
                                true
                            );
                        }
                    }
                >
                    + Create New Business
                </button>


                {/* SUCCESS */}

                {success && (

                    <div className="super-admin-success">

                        {success}

                    </div>

                )}


                {/* ERROR */}

                {error && !showCreateModal && (

                    <div className="error-message">

                        {error}

                    </div>

                )}


                {/* BUSINESS LIST */}

                <h3 className="section-title">
                    All Businesses
                </h3>


                {loading ? (

                    <div className="loading-state">
                        Loading businesses...
                    </div>

                ) : businesses.length === 0 ? (

                    <div className="empty-state">

                        <h3>
                            No businesses yet
                        </h3>

                        <p>
                            Create the first business
                            to get started.
                        </p>

                    </div>

                ) : (

                    <div className="super-admin-business-list">

                        {businesses.map(
                            business => {

                                const owner =
                                    business.users?.[0];


                                const isUpdating =
                                    updatingId ===
                                    business.id;


                                return (

                                    <article
                                        className="super-admin-business-card"
                                        key={
                                            business.id
                                        }
                                    >

                                        <div className="super-admin-business-top">

                                            <div className="super-admin-business-icon">

                                                {business
                                                    .business_name
                                                    ?.charAt(0)
                                                    .toUpperCase() ||
                                                    "B"}

                                            </div>


                                            <div className="super-admin-business-info">

                                                <h3>
                                                    {
                                                        business.business_name
                                                    }
                                                </h3>


                                                <span
                                                    className={
                                                        business.status ===
                                                        "ACTIVE"
                                                            ? "super-admin-status active"
                                                            : "super-admin-status inactive"
                                                    }
                                                >

                                                    {
                                                        business.status
                                                    }

                                                </span>

                                            </div>

                                        </div>


                                        <div className="super-admin-owner-box">

                                            <span>
                                                Business Owner
                                            </span>


                                            <strong>
                                                {
                                                    owner?.full_name ||
                                                    "No owner"
                                                }
                                            </strong>


                                            <small>
                                                Username:{" "}
                                                {
                                                    owner?.username ||
                                                    "-"
                                                }
                                            </small>

                                        </div>


                                        <button
                                            type="button"
                                            disabled={
                                                isUpdating
                                            }
                                            className={
                                                business.status ===
                                                "ACTIVE"
                                                    ? "super-admin-status-button deactivate"
                                                    : "super-admin-status-button activate"
                                            }
                                            onClick={
                                                () =>
                                                    handleStatusChange(
                                                        business
                                                    )
                                            }
                                        >

                                            {isUpdating
                                                ? "Updating..."
                                                : business.status ===
                                                  "ACTIVE"
                                                    ? "Deactivate Business"
                                                    : "Activate Business"}

                                        </button>

                                    </article>

                                );
                            }
                        )}

                    </div>

                )}

            </main>


            {/* ========================================
                CREATE BUSINESS MODAL
            ======================================== */}

            {showCreateModal && (

                <div className="super-admin-modal-overlay">

                    <div className="super-admin-modal">


                        <div className="super-admin-modal-header">

                            <div>

                                <p>
                                    PriceManager
                                </p>

                                <h2>
                                    Create Business
                                </h2>

                            </div>


                            <button
                                type="button"
                                onClick={
                                    closeCreateModal
                                }
                                disabled={
                                    creating
                                }
                            >
                                ×
                            </button>

                        </div>


                        <p className="super-admin-modal-text">

                            Create the business and
                            its owner account together.

                        </p>


                        {error && (

                            <div className="error-message">

                                {error}

                            </div>

                        )}


                        <form
                            onSubmit={
                                handleCreateBusiness
                            }
                        >

                            <div className="form-group">

                                <label>
                                    Business Name
                                </label>

                                <input
                                    type="text"
                                    name="business_name"
                                    value={
                                        form.business_name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter business name"
                                    disabled={
                                        creating
                                    }
                                />

                            </div>


                            <div className="form-group">

                                <label>
                                    Owner Full Name
                                </label>

                                <input
                                    type="text"
                                    name="owner_full_name"
                                    value={
                                        form.owner_full_name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter owner's full name"
                                    disabled={
                                        creating
                                    }
                                />

                            </div>


                            <div className="form-group">

                                <label>
                                    Owner Username
                                </label>

                                <input
                                    type="text"
                                    name="owner_username"
                                    value={
                                        form.owner_username
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Create owner username"
                                    autoComplete="off"
                                    disabled={
                                        creating
                                    }
                                />

                            </div>


                            <div className="form-group">

                                <label>
                                    Initial Password
                                </label>

                                <input
                                    type="password"
                                    name="owner_password"
                                    value={
                                        form.owner_password
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Minimum 8 characters"
                                    autoComplete="new-password"
                                    disabled={
                                        creating
                                    }
                                />

                            </div>


                            <button
                                type="submit"
                                className="primary-button"
                                disabled={
                                    creating
                                }
                            >

                                {creating
                                    ? "Creating..."
                                    : "Create Business"}

                            </button>

                        </form>

                    </div>

                </div>

            )}

        </div>
    );
}


export default SuperAdmin;